import { useState, useEffect, useMemo } from "react";
import { Edit } from "lucide-react";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import ConfirmAdvise from "@components/ui/ConfimDialogueAdvise";
import NavigationButtons from "./NavigationButtons";
import adviseMeImage from "@assets/images/AdviseMeSidePic.svg";
import stepsBack from "@assets/images/stepsBack.jpg";
import AdviseMeHeader from "./AdviseMeHeader";
import ReactModal from "react-modal";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  BmiCategory,
  GoalOverview,
  NutritionStratergy,
  WorkoutFocus,
  LongTerm,
} from "@utils/defaultImages";
import MedicalViewMultipSelectDropdown from "@components/ui/customDropdowns/MedicalViewMultipSelectDropdown";
import ExercisePlanMultiSelectDropdown from "@components/ui/customDropdowns/ExercisePlanMultiSelectDropdown";
import SportsGymnasticsIcon from "@mui/icons-material/SportsGymnastics";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TuneIcon from "@mui/icons-material/Tune";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const WeightGoalsForm = ({
  selectedTags,
  setSelectedTags,
  errors,
  setErrors,
  advisedConfirm,
  setAdviseConfirm,
  adviceTimer,
  isAdviceLoading,
  setOpen,
  user,
  loading,
  currentStep,
  setCurrentStep,
  validateStep,
  handleSubmit,
  handleInputChange,
  handleSelecChange,
  steps,
  dynamicOptions,
  handleMultiSelectChange,
  // These props will be used to update selectedTags when advice comes
  handleApplyGoal,
}) => {
  const [openDetails, setOpenDetails] = useState(false);
  const { t } = useTranslation();

  // Exercise time options
  const exerciseTimeOptions = [
    "15 Minutes",
    "30 Minutes",
    "45 Minutes",
    "60 Minutes",
    "90 Minutes",
    "90+ Minutes",
  ];

  // Days options
  const daysOptions = [
    "All Days",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Timeline options
  const timelineOptions = [
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
    "12",
  ];

  // Duration type options
  const durationTypeOptions = ["Week", "Month"];

  // Calculate daily calorie adjustment
  const calculatedDailyCalorieAdjustment = useMemo(() => {
    const target = Number(selectedTags?.targetDailyCalories);
    const maintenance = Number(selectedTags?.maintenanceCalories);
    if (
      !isNaN(target) &&
      !isNaN(maintenance) &&
      target !== null &&
      maintenance !== null
    ) {
      return Math.round(target - maintenance);
    }
    return 0;
  }, [selectedTags?.targetDailyCalories, selectedTags?.maintenanceCalories]);

  const nutrientData = [
    {
      label: t("BMI & Category"),
      key: "average_sleep_duration",
      icon: <BmiCategory className="w-[24px] h-[24px]" />,
      tabColor: "#F0FFFF",
      value: selectedTags?.["adviseMeDescription"]?.["BMI & Category"],
    },
    {
      label: t("Goal Overview"),
      key: "wake_ups_per_week",
      icon: <GoalOverview className="w-[24px] h-[24px]" />,
      tabColor: "#FFF0FB",
      value: selectedTags?.["adviseMeDescription"]?.["Goal Overview"],
    },
    {
      label: t("Nutrition Strategy"),
      key: "sleep_consistency_days",
      icon: <NutritionStratergy className="w-[24px] h-[24px]" />,
      tabColor: "#FFF6F0",
      value: selectedTags?.["adviseMeDescription"]?.["Nutrition Strategy"],
    },
    {
      label: t("Workout Focus"),
      key: "advice_followed",
      icon: <WorkoutFocus className="w-[24px] h-[24px]" />,
      tabColor: "#F5FFF0",
      value: selectedTags?.["adviseMeDescription"]?.["Workout Focus"],
    },
    {
      label: t("Long-Term Plan"),
      key: "advice_followed",
      icon: <LongTerm className="w-[24px] h-[24px]" />,
      tabColor: "#F5FFF0",
      value: selectedTags?.["adviseMeDescription"]?.["Long-Term Plan"],
    },
  ];

  // Helper functions for dynamic options
  const getDynamicDropdownOptions = (key) => {
    const options = dynamicOptions[key] || [];
    if (key === "difficultiesLevel") {
      return options.map((opt) => ({
        value: opt,
        label: opt.charAt(0).toUpperCase() + opt.slice(1),
      }));
    } else {
      return options.map((opt) => ({
        value: opt.id,
        label: opt.name,
      }));
    }
  };

  const getDisplayMap = (key) => {
    const options = dynamicOptions[key] || [];
    const displayMap = {};
    if (key === "difficultiesLevel") {
      options.forEach((opt) => {
        displayMap[opt] = opt.charAt(0).toUpperCase() + opt.slice(1);
      });
    } else {
      options.forEach((opt) => {
        displayMap[opt.id] = opt.name;
      });
    }
    return displayMap;
  };

  const getDisplayName = (key, categoryId = null) => {
    if (!categoryId || !dynamicOptions?.[key]) return categoryId || "";
    if (key === "difficultiesLevel") {
      return categoryId.charAt(0).toUpperCase() + categoryId.slice(1);
    }
    const item = dynamicOptions[key].find((item) => item?.id === categoryId);
    return item ? item.name : categoryId;
  };

  // Handle weight unit change
  const handleWeightUnitChange = (newUnit) => {
    if (selectedTags?.targetWeight) {
      const convertedWeight =
        newUnit === "Lbs"
          ? selectedTags.targetWeight * 2.20462
          : selectedTags.targetWeight / 2.20462;
      setSelectedTags((prev) => ({
        ...prev,
        targetWeight: Math.round(convertedWeight),
        targetWeightUnit: newUnit,
      }));
    } else {
      setSelectedTags((prev) => ({
        ...prev,
        targetWeightUnit: newUnit,
      }));
    }
  };

  // Handle removal functions
  const handleRemoveDay = (dayToRemove) => {
    const updatedDays = (selectedTags?.selectableDays || []).filter(
      (day) => day !== dayToRemove
    );
    handleMultiSelectChange("selectableDays", updatedDays);
  };

  const handleRemoveWorkoutCategory = (idToRemove) => {
    const updatedCategories = (selectedTags?.workoutsCategories || []).filter(
      (id) => id !== idToRemove
    );
    handleMultiSelectChange("workoutsCategories", updatedCategories);
  };

  // Render editable input field for advice section
  const renderEditableInput = (
    field,
    value,
    label,
    unit = null,
    disabled = false
  ) => {
    return (
      <div className="w-full">
        <span className="text-[#414651] text-[14px] sm:text-[16px] block mb-1">
          {label}
        </span>
        <div className="relative border-[none]">
          {field === "targetWeight" ? (
            <div className="relative">
              <TextField
                type="number"
                variant="outlined"
                fullWidth
                size="small"
                value={
                  value === null || value === undefined || value === ""
                    ? ""
                    : Math.round(Number(value))
                }
                onChange={(e) =>
                  handleInputChange(field, e.target.value, label)
                }
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                }}
                disabled={disabled || isAdviceLoading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    color: "black",
                    height: "40px",
                    "& fieldset": {
                      borderColor: errors.weight ? "#EF4444" : "#e5e7eb",
                      borderRadius: "0.375rem",
                    },
                    "&:hover fieldset": {
                      borderColor: errors.weight ? "#EF4444" : "#e5e7eb",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: errors.weight ? "#EF4444" : "#e5e7eb",
                      boxShadow: "none",
                      outline: "none",
                      borderWidth: "1px",
                    },
                    "& input::placeholder": {
                      fontSize: "0.9rem",
                      opacity: 1,
                      color: "#9CA3AF",
                    },
                    "&.Mui-disabled": {
                      // This is the disabled root style
                      "& fieldset": {
                        // Ensures the base disabled border is correct
                        borderColor: "#e5e7eb", // Example: set to a light gray
                        outline: "none",
                      },

                      // 👇 ADD THIS BLOCK TO REMOVE HOVER BORDER WHEN DISABLED
                      "&:hover fieldset": {
                        borderColor: "#e5e7eb", // Force the border color to match the non-hover disabled state
                        outline: "none",
                      },
                      // 👆 END OF NEW BLOCK
                    },
                  },
                }}
                InputProps={{
                  endAdornment:
                    field === "targetWeight" ? (
                      <InputAdornment position="end">
                        <div className="flex items-center mr-1">
                          {["kg", "Lbs"].map((unit) => (
                            <button
                              key={unit}
                              onClick={() => handleWeightUnitChange(unit)}
                              className={`px-2 py-0.5 border rounded-md mx-0.5 text-xs ${
                                selectedTags?.targetWeightUnit === unit
                                  ? "bg-blue-500 text-white"
                                  : "bg-white text-black border-gray-300"
                              }`}
                              type="button"
                              disabled={isAdviceLoading}
                            >
                              {unit}
                            </button>
                          ))}
                        </div>
                      </InputAdornment>
                    ) : unit ? (
                      <InputAdornment position="end">
                        <span className="text-gray-500 text-sm">{unit}</span>
                      </InputAdornment>
                    ) : null,
                }}
                placeholder={label}
              />
            </div>
          ) : (
            <input
              type="number"
              min="1"
              className={`w-full border p-2 rounded-md h-10 text-sm focus:outline-none ${
                errors[field] ? "border-red-500 text-red-500" : "text-black"
              }`}
              value={
                value === null || value === undefined || value === ""
                  ? ""
                  : Math.round(Number(value))
              }
              onChange={(e) => handleInputChange(field, e.target.value)}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              placeholder={label}
              disabled={isAdviceLoading}
            />
          )}
          {errors[field] && (
            <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
              {/* {errors[field]} */}
              {t("preferences.validation.fieldRequired", {
                field: label,
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render simple select field
  const renderSelectField = (field, value, label, options) => {
    const optionsArray = Array.isArray(options) ? options : [];

    return (
      <div className="w-full">
        <span className="text-[#414651] text-[14px] sm:text-[16px] block mb-1">
          {label}
        </span>
        <select
          className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
            errors[field] ? "border-red-500" : ""
          } ${isAdviceLoading ? "opacity-70" : ""}`}
          value={value || ""}
          onChange={(e) => handleSelecChange(field, e.target.value)}
          disabled={isAdviceLoading}
        >
          <option value="" disabled>
            {t("preferences.validation.onlyOne")}
          </option>
          {optionsArray.map((option) => (
            <option key={option} value={option}>
              {field === "durationType"
                ? t(`firstFormSteps.durationType.options.${option}`)
                : option}
            </option>
          ))}
        </select>
        {errors[field] && (
          <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
            {errors[field]}
          </div>
        )}
      </div>
    );
  };

  // Render dynamic select field
  const renderDynamicSelectField = (field, value, label) => {
    const options = getDynamicDropdownOptions(field);

    return (
      <div className="w-full">
        <span className="text-[#414651] text-[12px] font-semibold text-gray-700 uppercase block mb-1">
          {label}
        </span>
        <select
          className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
            isAdviceLoading ? "opacity-70 cursor-default" : ""
          }`}
          value={value || ""}
          onChange={(e) => handleSelecChange(field, e.target.value)}
          disabled={isAdviceLoading}
        >
          <option value="" disabled>
            {t("preferences.validation.onlyOne")}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  // Render exercise time select field
  const renderExerciseTimeSelect = (value, label) => {
    return (
      <div className="w-full">
        <span className="text-[#414651] text-[12px] font-semibold text-gray-700 uppercase block mb-1">
          {label}
        </span>
        <select
          className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
            isAdviceLoading ? "opacity-70 cursor-default" : ""
          }`}
          value={value || ""}
          onChange={(e) =>
            handleSelecChange("dailyExerciseTimePreference", e.target.value)
          }
          disabled={isAdviceLoading}
        >
          <option value="" disabled>
            {t("preferences.validation.onlyOne")}
          </option>
          {exerciseTimeOptions.map((option) => (
            <option key={option} value={option}>
              {/* {option} */}
              {t(
                `allPreferencesModule.options.dailyExerciseTimePreference.${option}`
              )}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <>
      <div className="hidden sm:block mb-5">
        <AdviseMeHeader selectedTags={selectedTags} user={user} t={t} />
      </div>

      <h2 className="text-[20px] md:text-[30px] md:text-center font-[600] text-black-600 md:px-4 mt-2 md:mt-0">
        {t("firstFormSteps.lastStepHeading")}
      </h2>
      <p className="text-[#0575E6] text-[16px] md:text-center  md:px-4">
        {t("firstFormSteps.lastStepSubHeading")}
      </p>

      <div className="flex justify-center">
        <div className="grid lg:grid-cols-5 gap-4 md:gap-3 w-full mt-3">
          {/* Left Column - Input Fields (3 columns on large screens) */}
          <div className="lg:col-span-3 md:px-4">
            <h2 className="text-[18px] md:text-[24px] font-[600] text-black-600 mb-2 leading-normal">
              {t("firstFormSteps.automaticGoalHeading")}
            </h2>
            <button
              onClick={() => setOpen(true)}
              className={`px-4 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-md w-full ${
                isAdviceLoading && "opacity-70"
              }`}
              disabled={isAdviceLoading}
            >
              {isAdviceLoading
                ? adviceTimer + "s"
                : t("firstFormSteps.getAdvise")}
            </button>

            <div className="flex justify-between items-center my-3 lg:my-4">
              <h2 className="text-[18px] md:text-[24px] font-[600] text-black-600">
                {t("firstFormSteps.manualGoal")}
              </h2>
            </div>

            {/* Input Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* First row: Target Weight, Timeline */}
              <div>
                {renderEditableInput(
                  "targetWeight",
                  selectedTags?.targetWeight,
                  t(`firstFormSteps.targetWeight.label`)
                )}
              </div>

              {/* Second row: Goal Body Fat, Goal Waist Circumference */}
              <div>
                {renderEditableInput(
                  "goalBodyFat",
                  selectedTags?.goalBodyFat,
                  t("Goal Body Fat"),
                  "%"
                )}
              </div>

              <div>
                {renderEditableInput(
                  "goalWaistCircumference",
                  selectedTags?.goalWaistCircumference,
                  t("Goal Waist Circumference"),
                  "cm"
                )}
              </div>

              {/* Third row: Duration Type, Maintenance Calories */}

              <div>
                {renderEditableInput(
                  "maintenanceCalories",
                  selectedTags?.maintenanceCalories,
                  t(`firstFormSteps.maintenanceCalories.label`)
                )}
              </div>

              {/* Fourth row: Target Daily Calories, Daily Calorie Adjustment */}
              <div>
                {renderEditableInput(
                  "targetDailyCalories",
                  selectedTags?.targetDailyCalories,
                  t(`firstFormSteps.targetDailyCalories.label`)
                )}
              </div>

              <div>
                {renderEditableInput(
                  "dailyCalorieAdjustment",
                  calculatedDailyCalorieAdjustment,
                  t(`preferences.fields.dailyCalorieAdjustment`),
                  "Kcal",
                  true
                )}
              </div>

              <div>
                {renderEditableInput(
                  "goalSteps",
                  selectedTags?.goalSteps,
                  t(`preferences.fields.goalSteps`),
                  
                )}
              </div>

              <div>
                {renderEditableInput(
                  "detailTargetBurnCalories",
                  selectedTags?.detailTargetBurnCalories,
                  t(`preferences.fields.detailTargetBurnCalories`),
                  
                )}
              </div>

              <div>
                {renderEditableInput(
                  "targetActiveTime",
                  selectedTags?.targetActiveTime,
                  t(`preferences.fields.targetActiveTime`),
                  
                )}
              </div>

              <div>
                {renderSelectField(
                  "durationType",
                  selectedTags?.durationType,
                  t(`firstFormSteps.durationType.label`),
                  durationTypeOptions
                )}
              </div>

              <div>
                {renderSelectField(
                  "timeline",
                  selectedTags?.timeline,
                  t(`firstFormSteps.timeline.label`),
                  timelineOptions
                )}
              </div>
            </div>

            {/* Original weight options dropdowns */}
            {/* <div>
              <div className="grid md:grid-cols-2 gap-2 md:gap-[30px]">
                {weightOptions?.map(
                  ({ label, key, type, options }) =>
                    type === "select" && (
                      <>
                        {advisedConfirm?.[key] ? (
                          <div key={key} className="mb-2 lg:mb-8 mt-3">
                            <span className="text-[#414651] text-[14px]">
                              {t(`firstFormSteps.${key}.label`)}
                            </span>
                            <div className="flex items-center gap-2 relative w-full">
                              <input
                                className={`border p-2 rounded-md w-full h-10 text-sm focus:outline-none ${
                                  isAdviceLoading
                                    ? "opacity-70 cursor-not-allowed"
                                    : ""
                                }`}
                                value={t(
                                  `firstFormSteps.${key}.options.${selectedTags[key]}` ||
                                    ""
                                )}
                                disabled={
                                  advisedConfirm?.[key] || isAdviceLoading
                                }
                              />
                              {advisedConfirm?.[key] && (
                                <button
                                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#0575e6]"
                                  onClick={() =>
                                    setAdviseConfirm((prev) => ({
                                      ...prev,
                                      [key]: false,
                                    }))
                                  }
                                >
                                  <Edit />
                                </button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div key={key} className="mb-2 lg:mb-8 mt-3">
                            <span className="text-[#414651] text-[14px]">
                              {t(`firstFormSteps.${key}.label`)}
                            </span>
                            <select
                              className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
                                errors[key] ? "border-red-500" : ""
                              } ${
                                isAdviceLoading
                                  ? "opacity-70 cursor-not-allowed"
                                  : ""
                              }`}
                              value={
                                Array.isArray(selectedTags[key])
                                  ? selectedTags[key][
                                      selectedTags[key].length - 1
                                    ]
                                  : selectedTags[key] || ""
                              }
                              onChange={(e) =>
                                handleSelecChange(key, e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (["e", "E", "+", "-"].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              disabled={isAdviceLoading}
                            >
                              <option value="" disabled>
                                {t("preferences.validation.onlyOne")}
                              </option>
                              {options.map((option) => (
                                <option key={option} value={option}>
                                  {t(`firstFormSteps.${key}.options.${option}`)}
                                </option>
                              ))}
                            </select>
                            {errors[key] && (
                              <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                                {errors[key]}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )
                )}
              </div>
            </div> */}
          </div>

          {/* Right Column - Professional Advice (2 columns on large screens) */}
          <div className="lg:col-span-2 lg:mt-[42px]">
            {selectedTags?.["adviseMeDescription"] &&
              typeof selectedTags["adviseMeDescription"] === "object" &&
              Object.keys(selectedTags["adviseMeDescription"]).length > 0 && (
                <button
                  onClick={() => setOpenDetails(true)}
                  className={`mt-3 px-4 py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-md w-full block sm:hidden`}
                >
                  {t(`firstFormSteps.viewMoreDetails`)}
                </button>
              )}

            <div className="hidden md:block">
              {selectedTags?.["adviseMeDescription"] &&
              typeof selectedTags["adviseMeDescription"] === "object" &&
              Object.keys(selectedTags["adviseMeDescription"]).length > 0 ? (
                <div className="py-4 px-7 rounded-lg bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] h-full">
                  <div className="flex justify-center mb-4">
                    <p className="bg-[#64DDFF] text-white rounded-full text-[12px] sm:text-[16px] font-[Lato] font-[600] py-1 px-4 whitespace-nowrap overflow-hidden text-ellipsis">
                      {t("Our Professional Advice For You")}
                    </p>
                  </div>
                  <div
                    id="scrollable-quiz-container"
                    className="h-full max-h-[500px] overflow-y-auto pr-2 custom-scrollbar space-y-2"
                  >
                    {nutrientData.map(
                      ({ label, icon, value, tabColor }, index) => (
                        <div
                          key={index}
                          className="py-5 px-6 rounded-[17px] bg-white border border-[#E6E6E6] rounded-[16px] flex flex-col xl:flex-col gap-2"
                          style={{
                            boxShadow: "7px 7px 9.5px 0px #0000000D",
                          }}
                        >
                          <div className="relative">
                            <div
                              className="absolute -left-3 -top-1 rounded-full w-12 h-12 flex items-center justify-center border border-[#0575e6]"
                              style={{
                                backgroundColor: tabColor,
                              }}
                            >
                              {icon}
                            </div>
                            <p className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-[12px] xl:text-[16px] font-[600] text-[#fff] py-2 px-10 rounded-full text-center xl:text-start xl:min-w-[222px] xl:max-w-[222px]">
                              {label}
                            </p>
                          </div>
                          <div className="">
                            <p className="text-[10px] md:text-[16px] font-[500] text-[#6C6A6A]">
                              {value}
                            </p>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  <style jsx>{`
                    .custom-scrollbar::-webkit-scrollbar {
                      width: 12px;
                    }

                    .custom-scrollbar::-webkit-scrollbar-track {
                      background-color: #fff !important;
                      border-radius: 10px;
                      margin: 4px 0;
                    }

                    .custom-scrollbar::-webkit-scrollbar-thumb {
                      background: linear-gradient(
                        150deg,
                        #0771ef 12.89%,
                        #15aafc 85.22%
                      );
                      border-radius: 10px;
                      border: 1px solid rgba(255, 255, 255, 0.2);
                      box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.3);
                    }
                  `}</style>
                </div>
              ) : (
                <img
                  src={adviseMeImage}
                  className=" object-cover rounded-[15px] w-full min-h-[500px] max-h-[638px]  hidden sm:block "
                  alt="step"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Exercise Preferences Section */}
      <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          {t("Exercise Preferences")}
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Workout Categories Card */}
          <div className="col-span-2 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <SportsGymnasticsIcon className="text-blue-500 mr-2" />
              <span className="text-xs font-semibold text-gray-700 uppercase">
                {t("Workout Categories")}
              </span>
            </div>
            <ExercisePlanMultiSelectDropdown
              options={getDynamicDropdownOptions("workoutsCategories").map(
                (opt) => opt.value
              )}
              selectedValues={selectedTags?.workoutsCategories || []}
              onSelect={(values) =>
                handleMultiSelectChange("workoutsCategories", values)
              }
              label={t("Workout Categories")}
              hasError={errors?.workoutsCategories}
              placeholder={t("preferences.validation.onlyOne")}
              isEditable={!isAdviceLoading}
              isFistForm={true}
              displayMap={getDisplayMap("workoutsCategories")}
              fieldKey="workoutsCategories"
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedTags?.workoutsCategories &&
                selectedTags?.workoutsCategories.length > 0 &&
                selectedTags?.workoutsCategories.map((categoryId) => (
                  <span
                    key={categoryId}
                    className="px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800 border border-blue-200 flex items-center space-x-1 cursor-pointer transition duration-150 ease-in-out hover:bg-blue-200"
                  >
                    <span>
                      {getDisplayName("workoutsCategories", categoryId)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveWorkoutCategory(categoryId)}
                      className="ml-1 w-4 h-4 rounded-full text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label={`Remove workout category`}
                      disabled={isAdviceLoading}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </span>
                ))}
            </div>
          </div>

          {/* Equipment Category Card */}
          <div className="col-span-2 lg:col-span-1 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <FitnessCenterIcon className="text-green-500 mr-2" />
              <span className="text-xs font-semibold text-gray-700 uppercase">
                {t("Equipment Category")}
              </span>
            </div>
            {renderDynamicSelectField(
              "equipmentCategories",
              selectedTags?.equipmentCategories
              // t("Equipment Category")
            )}
            <div className="mt-2">
              {selectedTags?.equipmentCategories && (
                <span className="px-2 py-1 text-xs font-medium rounded-md bg-green-100 text-green-800 border border-green-200">
                  {getDisplayName(
                    "equipmentCategories",
                    selectedTags?.equipmentCategories
                  )}
                </span>
              )}
            </div>
            {errors?.equipmentCategories && (
              <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                {errors?.equipmentCategories}
              </div>
            )}
          </div>

          {/* Difficulty Level Card */}
          <div className="col-span-2 lg:col-span-1 p-4 bg-purple-50 border-l-4 border-purple-500 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <TuneIcon className="text-purple-500 mr-2" />
              <span className="text-xs font-semibold text-gray-700 uppercase">
                {t("Difficulty Level")}
              </span>
            </div>
            {renderDynamicSelectField(
              "difficultiesLevel",
              selectedTags?.difficultiesLevel
              // t("Difficulty Level")
            )}
            <div className="mt-2">
              {selectedTags?.difficultiesLevel && (
                <span className="px-2 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-800 border border-purple-200">
                  {getDisplayName(
                    "difficultiesLevel",
                    selectedTags?.difficultiesLevel
                  )}
                </span>
              )}
              {errors?.difficultiesLevel && (
                <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                  {errors?.difficultiesLevel}
                </div>
              )}
            </div>
          </div>

          {/* Daily Time Preference Card */}
          <div className="col-span-2 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <AccessTimeIcon className="text-orange-500 mr-2" />
              <span className="text-xs font-semibold text-gray-700 uppercase">
                {t("Daily Exercise Time Preference")}
              </span>
            </div>
            {renderExerciseTimeSelect(
              selectedTags?.dailyExerciseTimePreference
              // t("Daily Exercise Time Preference")
            )}
            <div className="mt-2">
              {selectedTags?.dailyExerciseTimePreference && (
                <span className="px-2 py-1 text-xs font-medium rounded-md bg-orange-100 text-orange-800 border border-orange-200">
                  {/* {selectedTags?.dailyExerciseTimePreference} */}
                  {t(
                    `allPreferencesModule.options.dailyExerciseTimePreference.${selectedTags?.dailyExerciseTimePreference}`
                  )}
                </span>
              )}
              {errors?.dailyExerciseTimePreference && (
                <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                  {errors?.dailyExerciseTimePreference}
                </div>
              )}
            </div>
          </div>

          {/* Selectable Days Card */}
          <div className="col-span-full md:col-span-2 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <CalendarMonthIcon className="text-red-500 mr-2" />
              <span className="text-xs font-semibold text-gray-700 uppercase">
                {t("Selectable Days")}
              </span>
            </div>
            <MedicalViewMultipSelectDropdown
              options={daysOptions?.map((opt) => ({
                value: opt,
                label: t(`allPreferencesModule.options.selectableDays.${opt}`),
              }))}
              selectedValues={selectedTags?.selectableDays || []}
              onSelect={(selectedOptions) => {
                const originalValues = selectedOptions.map((opt) =>
                  typeof opt === "object" ? opt.value : opt
                );
                handleMultiSelectChange("selectableDays", originalValues);
              }}
              isFistForm={true}
              hasError={errors.selectableDays}
              label={t("Selectable Days")}
              placeholder={t("preferences.validation.onlyOne")}
              isEditable={!isAdviceLoading}
            />
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedTags?.selectableDays &&
                selectedTags?.selectableDays.length > 0 &&
                selectedTags?.selectableDays.map((day) => (
                  <span
                    key={day}
                    className="px-2 py-1 text-xs font-medium rounded-md bg-red-100 text-red-800 border border-red-200 flex items-center space-x-1 cursor-pointer transition duration-150 ease-in-out hover:bg-red-200"
                  >
                    <span>
                      {" "}
                      {t(`allPreferencesModule.options.selectableDays.${day}`)}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveDay(day)}
                      className="ml-1 w-4 h-4 rounded-full text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                      aria-label={`Remove ${day}`}
                      disabled={isAdviceLoading}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </span>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="mt-6">
        <NavigationButtons
          currentStep={currentStep}
          steps={steps}
          loading={loading}
          isAdviceLoading={isAdviceLoading}
          validateStep={validateStep}
          setCurrentStep={setCurrentStep}
          handleSubmit={handleSubmit}
          selectedTags={selectedTags}
        />
      </div>

      {/* Mobile Details Modal */}
      <ReactModal
        isOpen={openDetails}
        className="rounded-lg w-[100%] max-h-[80%] font-[Lato] shadow-xl border border-gray-200 relative outline-none overflow-y-auto max-w-[90%] bg-white flex justify-center"
        overlayClassName="fixed inset-0 flex items-center justify-center backdrop-blur-sm"
        onRequestClose={() => setOpenDetails(false)}
      >
        <div className="p-[15px]">
          <div className="flex items-center justify-end mb-2">
            <button onClick={() => setOpenDetails(false)}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="24" height="24" rx="12" fill="#0575E6" />
                <path
                  d="M16 8L8 16"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 8L16 16"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="py-4 px-3 md:px-7 rounded-lg bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] min-h-[450px]">
            <div
              id="scrollable-quiz-container"
              className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar space-y-2"
            >
              {nutrientData.map(({ label, icon, value, tabColor }, index) => (
                <div
                  key={index}
                  className="py-4 px-4 md:py-5 md:px-6 rounded-[16px] bg-white border border-[#E6E6E6] flex flex-col gap-2"
                  style={{
                    boxShadow: "7px 7px 9.5px 0px #0000000D",
                  }}
                >
                  <div className="relative">
                    <div
                      className={`absolute border border-[#0575e6] -left-3 -top-1 rounded-full min-w-12 min-h-12 flex items-center justify-center border`}
                      style={{
                        backgroundColor: tabColor,
                      }}
                    >
                      {icon}
                    </div>
                    <p className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-[14px] lg:text-[16px] font-[600] text-[#fff] py-2 px-8 md:px-10 rounded-full text-center lg:text-start min-w-[180px] md:min-w-[200px] max-w-full">
                      {label}
                    </p>
                  </div>
                  <div className="mt-2 md:mt-0">
                    <p className="-[14px] lg:text-[16px] font-[500] text-[#6C6A6A] font-[Lato] leading-relaxed">
                      {value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <style jsx>{`
              .custom-scrollbar::-webkit-scrollbar {
                width: 12px;
              }

              .custom-scrollbar::-webkit-scrollbar-track {
                background-color: #fff !important;
                border-radius: 10px;
                margin: 4px 0;
              }

              .custom-scrollbar::-webkit-scrollbar-thumb {
                background: linear-gradient(
                  150deg,
                  #0771ef 12.89%,
                  #15aafc 85.22%
                );
                border-radius: 10px;
                border: 1px solid rgba(255, 255, 255, 0.2);
                box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.3);
              }
            `}</style>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default WeightGoalsForm;
